import React, {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import Storage from '../../services/Storage';
import {LOCATION_KEY, PREVIOUS_LOCATION, SCROLL_POSITION} from '../../constants/storageKeys';
import * as types from '../../constants/ActionTypes';
import {getFilters, getInterventions, getPagination, isLoadingInterventions} from '../../selectors/interventionsSelector';

import TasksTableHeader from './table/TasksTableHeader';
import TasksTable from './table/TasksTable';
import TasksTableFooter from './table/TasksTableFooter';

import './Tasks.css';
import {getInterventionInitFiltersWithUser, INIT_INTERVENTION_PAGINATION} from "../../constants/filters";

const TWO_MINUTES = 2 * 60 * 1000;

const Tasks = ({dispatchFetchInterventions, filters, location, pagination}) => {

  const intervalRef = useRef(null);
  const refreshListRef = useRef(null);

  refreshListRef.current = () => {
    updateContent(pagination.currentPage, pagination.currentPageSize, filters);
  };

  useEffect(() => {
    const locationKey = Storage.getSessionItem(LOCATION_KEY);
    const previousLocation = Storage.getSessionItem(PREVIOUS_LOCATION);
    const shouldRestore = location.key ? location.key === locationKey : previousLocation === location.pathname;

    shouldRestore ? fetchWithSavedFilters() : fetchWithDefaultFilters();

    // runs when component mounts
    resetInterval();

    // runs when component unmounts
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  const fetchWithDefaultFilters = () => {
    dispatchFetchInterventions({page: 0, size: INIT_INTERVENTION_PAGINATION.currentPageSize}, getInterventionInitFiltersWithUser());
    window.scrollTo(0, 0);
  };

  const fetchWithSavedFilters = () => {
    dispatchFetchInterventions({page: pagination.currentPage - 1, size: pagination.currentPageSize,}, filters);
    restoreScrollPosition();
  };

  const resetInterval = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => refreshListRef.current(), TWO_MINUTES);
  }

  const restoreScrollPosition = () => {
    const scrollPosition = Storage.getSessionItem(SCROLL_POSITION);
    if (scrollPosition) {
      window.scrollTo(0, parseInt(scrollPosition, 10));
    }
    Storage.removeSessionItems([SCROLL_POSITION, LOCATION_KEY, PREVIOUS_LOCATION]);
  };

  const updateContent = (page, size, rawFilter) => {
    dispatchFetchInterventions({
      page: page - 1,
      size
    }, {
      ...filters,
      ...rawFilter,
    });
    resetInterval();
  };

  const onListRefresh = () => refreshListRef.current();

  const onContentUpdate = (filters) => updateContent(1, pagination.currentPageSize, filters);

  const onPaginationUpdate = (page, size) => updateContent(page, size, filters);

  const onShowArchivedChange = (value) => updateContent(1, pagination.currentPageSize, {showArchived: value});

  const onShowOnlyMineChange = (value) => updateContent(1, pagination.currentPageSize, {showOnlyMine: value});

  return (
    <div className="box__body">
      <TasksTableHeader
        contentUpdater={onContentUpdate}
        showArchived={filters.showArchived}
        showOnlyMine={filters.showOnlyMine}
        onShowArchived={onShowArchivedChange}
        onShowOnlyMine={onShowOnlyMineChange}
        onListRefresh={onListRefresh}
        fetchWithDefaultFilters={fetchWithDefaultFilters}
      />
      <TasksTable contentUpdater={onContentUpdate} showArchived={filters.showArchived}/>
      <TasksTableFooter contentUpdater={onPaginationUpdate}/>
    </div>
  );

}

const mapStateToProps = state => ({
  filters: getFilters(state),
  interventions: getInterventions(state),
  loading: isLoadingInterventions(state),
  pagination: getPagination(state)
});


const mapDispatchToProps = dispatch => ({
  dispatchFetchInterventions: (parameters, filters) => dispatch({type: types.FETCH_INTERVENTIONS, parameters, filters})
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Tasks));
